import { useState } from "react";
import { Logo } from "../../atoms/Logo.jsx";
import { VoterBadge } from "../../molecules/VoterBadge.jsx";
import { LocationEditModal } from "../../molecules/LocationEditModal.jsx";

/**
 * Top navigation bar + mobile bottom nav.
 *
 * Admin mode: Personas | Rewards | + New | Log out (top bar only)
 * Voter mode:
 *   Desktop (>640px): Logo | Officials | Agencies | Shop | XP badge | VoterBadge
 *   Mobile (≤640px):  Top bar = Logo + XP badge
 *                     Bottom nav = Officials | Agencies | Shop | Profile
 *
 * v1.28.3: desktop "Elected Officials" + mobile "Home" (🏠) → single shared
 * "Officials" label with 👥 icon on both breakpoints. Keeps parity with the
 * 🏛️ Agencies tile and removes the mismatch where desktop and mobile
 * called the same view different things.
 */
export function Nav({ view, admin, voter, voterXp, rewards, onHome, onSetView, onAdminLogout, onVoterUpdate }) {
  const [showLocationEdit, setShowLocationEdit] = useState(false);

  // Calculate cheapest reward cost for XP colour coding
  const cheapestRewardCost = rewards?.length
    ? Math.min(...rewards.map(r => r.cost ?? r.xpCost ?? Infinity))
    : Infinity;
  const canAfford = voterXp >= cheapestRewardCost && cheapestRewardCost < Infinity;

  const xpBadge = (
    <button
      onClick={() => onSetView("shop")}
      style={{
        display:"flex",alignItems:"center",gap:4,padding:"4px 10px",
        background: canAfford ? "#22c55e12" : "#f59e0b12",
        border:`1px solid ${canAfford ? "#22c55e30" : voterXp > 0 ? "#f59e0b30" : "#1e2d45"}`,
        borderRadius:8,cursor:"pointer",
        animation: canAfford ? "xpGlow 2s ease-in-out infinite" : "none",
      }}
    >
      <span style={{fontSize:11}}></span>
      <span style={{
        color: voterXp === 0 ? "#64748b" : canAfford ? "#22c55e" : "#f59e0b",
        fontWeight:700,fontSize:12
      }}>{voterXp} XP</span>
    </button>
  );

  // NAV-6: map view states to URL paths
  // v1.28.0 (AGCY): `agency-dir` maps to /agencies for the voter Agencies directory.
  const viewToUrl = { dir: "/", "agency-dir": "/agencies", shop: "/shop", "voter-profile": "/me", list: "/", create: "/admin/create", analytics: "/admin/analytics", rewards: "/admin/rewards" };

  const navBtn = (label, targetView) => (
    <button
      onClick={() => { if (viewToUrl[targetView]) window.history.pushState({}, "", viewToUrl[targetView]); onSetView(targetView); }}
      style={{
        padding:"6px 14px",fontFamily:"inherit",fontSize:13,fontWeight:600,cursor:"pointer",borderRadius:8,
        background: view === targetView ? "#1e3a5f" : "transparent",
        color:       view === targetView ? "#60a5fa"  : "#64748b",
        border:     `1px solid ${view === targetView ? "#3b82f640" : "transparent"}`,
        transition: "background .15s, color .15s",
      }}
    >
      {label}
    </button>
  );

  const bottomNavItem = (icon, label, targetView) => (
    <button
      key={targetView}
      onClick={() => { if (viewToUrl[targetView]) window.history.pushState({}, "", viewToUrl[targetView]); onSetView(targetView); }}
      style={{
        flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:2,
        background:"none",border:"none",cursor:"pointer",padding:"6px 0",fontFamily:"inherit",
        color: view === targetView ? "#60a5fa" : "#64748b",
      }}
    >
      <span style={{fontSize:18}}>{icon}</span>
      <span style={{fontSize:9,fontWeight:600}}>{label}</span>
    </button>
  );

  return (
    <>
      {/* Top bar */}
      <nav style={{
        borderBottom:"1px solid #1e2d45",padding:"0 18px",
        display:"flex",alignItems:"center",justifyContent:"space-between",
        height:54,background:"#090e1a",flexShrink:0,zIndex:100,
      }}>
        {/* Logo */}
        <button
          onClick={onHome}
          style={{background:"none",border:"none",cursor:"pointer",display:"flex",alignItems:"center",gap:8,padding:0}}
        >
          <Logo size={16} />
        </button>

        {/* Desktop centre nav tabs — hidden on mobile */}
        {/* v1.28.0 (AGCY): "Agencies" sits between Elected Officials and Shop. */}
        {!admin && (
          <div className="qm-nav-desktop" style={{display:"flex",alignItems:"center",gap:2}}>
            {navBtn("👥 Officials", "dir")}
            {navBtn("🏛️ Agencies", "agency-dir")}
            {navBtn("🛍 Shop", "shop")}
          </div>
        )}

        {/* Right side */}
        <div style={{display:"flex",alignItems:"center",gap:7}}>
          {admin ? (
            <>
              <button onClick={() => { window.history.pushState({}, "", "/"); onSetView("list"); }}    style={{padding:"6px 12px",background:view==="list"?"#1e3a5f":"#1e2d45",color:view==="list"?"#60a5fa":"#94a3b8",border:`1px solid ${view==="list"?"#3b82f640":"#2d3f5a"}`,borderRadius:7,fontSize:12,cursor:"pointer",fontFamily:"inherit",fontWeight:600}}>Personas</button>
              <button onClick={() => { window.history.pushState({}, "", "/admin/rewards"); onSetView("rewards"); }} style={{padding:"6px 12px",background:view==="rewards"?"#1e3a5f":"#1e2d45",color:view==="rewards"?"#60a5fa":"#94a3b8",border:`1px solid ${view==="rewards"?"#3b82f640":"#2d3f5a"}`,borderRadius:7,fontSize:12,cursor:"pointer",fontFamily:"inherit",fontWeight:600}}>Rewards</button>
              <button onClick={() => { window.history.pushState({}, "", "/admin/create"); onSetView("create"); }}  style={{padding:"6px 12px",background:"#1e2d45",color:"#94a3b8",border:"1px solid #2d3f5a",borderRadius:7,fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>+ New</button>
              <button onClick={onAdminLogout}              style={{padding:"6px 12px",background:"#ef444418",color:"#ef4444",border:"1px solid #ef444430",borderRadius:7,fontSize:12,cursor:"pointer",fontFamily:"inherit",fontWeight:600}}>Log out</button>
            </>
          ) : (
            <>
              {xpBadge}
              {/* Desktop-only: voter badge + profile icon */}
              {voter && (
                <span className="qm-nav-desktop" style={{display:"flex",alignItems:"center",gap:7}}>
                  <VoterBadge voter={voter} onEdit={() => setShowLocationEdit(true)}/>
                  <button
                    onClick={() => { window.history.pushState({}, "", "/me"); onSetView("voter-profile"); }}
                    aria-label="My Account"
                    style={{
                      width:32,height:32,borderRadius:"50%",cursor:"pointer",fontFamily:"inherit",
                      display:"flex",alignItems:"center",justifyContent:"center",fontSize:15,
                      background: view === "voter-profile" ? "#1e3a5f" : "#1e2d45",
                      color:      view === "voter-profile" ? "#60a5fa" : "#94a3b8",
                      border:     `1px solid ${view === "voter-profile" ? "#3b82f640" : "#2d3f5a"}`,
                      transition: "background .15s, color .15s",
                    }}
                  >
                    👤
                  </button>
                </span>
              )}
              {/* Admin login accessible via /admin URL only — no public nav link */}
            </>
          )}
        </div>
      </nav>

      {/* Mobile bottom nav — voter mode only */}
      {!admin && voter && (
        <div className="qm-bottom-nav" style={{
          position:"fixed",bottom:0,left:0,right:0,
          background:"#090e1a",borderTop:"1px solid #1e2d45",
          display:"none", // shown via CSS media query
          paddingBottom:"max(4px, env(safe-area-inset-bottom, 4px))",
          zIndex:100,
        }}>
          <div style={{display:"flex",alignItems:"center"}}>
            {/* v1.28.0 (AGCY): Agencies tile added between Home and Shop.
                v1.28.3: "🏠 Home" → "👥 Officials" to match desktop. The
                voter-facing root (/) IS the Officials directory — calling
                it "Home" on mobile while desktop called it "Elected
                Officials" was a copy inconsistency. */}
            {bottomNavItem("👥", "Officials", "dir")}
            {bottomNavItem("🏛️", "Agencies", "agency-dir")}
            {bottomNavItem("🛍️", "Shop", "shop")}
            {bottomNavItem("👤", "Me", "voter-profile")}
          </div>
        </div>
      )}

      {/* Location edit modal */}
      {showLocationEdit && voter && (
        <LocationEditModal
          voter={voter}
          onClose={() => setShowLocationEdit(false)}
          onUpdate={onVoterUpdate}
        />
      )}
    </>
  );
}
